import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { Typography } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { toast } from 'react-toastify';
import useStyles from './RetirarControleExpiracaoProcesso.style';
import {
  TITULO_PAGINA,
  SUBTITULO_PAGINA,
  MAX_LENGTH_NUM_PROCESSO,
  MINIMO_PALAVRAS_JUSTIFICATIVA,
  MAX_COUNTER_JUSTIFICATIVA,
  LABEL_NUMERO_PROCESSO,
  LABEL_NOME_CLIENTE,
  LABEL_JUSTIFICATIVA,
  LABEL_RETIRAR_CONTROLE,
  MENSAGEM_ERRO_BUSCA_PROCESSO,
  MENSAGEM_BLOQUEIO_PROCESSO_BAIXADO,
  MENSAGEM_BLOQUEIO_JA_FORA_DO_CONTROLE,
  MENSAGEM_BLOQUEIO_CLIENTE_SEM_CONTROLE,
  MENSAGEM_CONFIRMACAO_ALTERACAO,
  MENSAGEM_SUCESSO_ALTERACAO,
  MENSAGEM_ERRO_ALTERACAO,
  MENSAGEM_ERRO_HISTORICO_AUTOMATICO,
} from './retirarControleExpiracaoProcesso.constants';
import LoadingUI from '../../../components/UI/Loading/LoadingUI';
import WrapperSubTitleForm from '../../../components/UI/WrapperSubTitleForm/WrapperSubTitleForm';
import Grid24 from '../../../components/UI/Grid24/Grid24';
import NumberInput from '../../../components/UI/Field/Number/NumberInput';
import InputField from '../../../components/UI/Field/Input/InputField';
import OptionDialogNew from '../../../components/UI/Dialogs/OptionDialog/OptionDialogNew';
import { SearchButton } from '../../../components/SearchButton/SeachButton';
import { ButtonUI, TextareaField } from '../../../components';
import { getExceptionMessage, possuiMinimoPalavras } from '../../../helpers';
import {
  findControleExpiracaoAtualByNumProcesso,
  retirarControleExpiracaoDoProcesso,
} from '../../../services/core/manutencao/manutencao.service';
import { inserirNovoHistorico } from '../../../services/core/processos/processosService';
import { tiposHistoricos } from '../../../types/tiposHistoricoTypes';

// Mesma ordem de validação usada pelo backend real (abe-web-core-spring, Etapa 5): processo
// baixado -> processo já retirado -> cliente sem controle de expiração. Mantida idêntica aqui
// para que o bloqueio exibido nunca divirja do que o PUT revalidaria para o mesmo processo.
function obterMensagemBloqueio(controleExpiracaoAtual) {
  if (controleExpiracaoAtual.processoBaixado) {
    return MENSAGEM_BLOQUEIO_PROCESSO_BAIXADO;
  }
  if (controleExpiracaoAtual.retiradoControleExpiracao) {
    return MENSAGEM_BLOQUEIO_JA_FORA_DO_CONTROLE;
  }
  if (!controleExpiracaoAtual.clienteControlaExpiracao) {
    return MENSAGEM_BLOQUEIO_CLIENTE_SEM_CONTROLE;
  }
  return null;
}

export default function RetirarControleExpiracaoProcesso() {
  const style = useStyles();
  const idUsuario = useSelector(state => state.authentication.userInformation.idUsuario);

  const [loading, setLoading] = useState(false);
  const [numProcesso, setNumProcesso] = useState('');
  const [controleExpiracaoAtual, setControleExpiracaoAtual] = useState(null);
  const [dialogBloqueioFechado, setDialogBloqueioFechado] = useState(false);
  const [justificativa, setJustificativa] = useState('');
  const [confirmacaoAberta, setConfirmacaoAberta] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const buscarProcesso = useCallback((numero) => {
    setLoading(true);
    setControleExpiracaoAtual(null);
    setDialogBloqueioFechado(false);
    setJustificativa('');
    return findControleExpiracaoAtualByNumProcesso(numero).then((response) => {
      const dados = response?.data;
      if (!dados?.idProcesso || dados.processoBaixado === undefined
        || dados.retiradoControleExpiracao === undefined
        || dados.clienteControlaExpiracao === undefined) {
        throw new Error('Resposta da busca de processo fora do contrato esperado');
      }
      setControleExpiracaoAtual(dados);
    }).catch((exception) => {
      setControleExpiracaoAtual(null);
      toast.error(getExceptionMessage(exception) || MENSAGEM_ERRO_BUSCA_PROCESSO);
    }).finally(() => {
      setLoading(false);
    });
  }, []);

  const handleSubmitSearchForm = useCallback((e) => {
    e.preventDefault();
    buscarProcesso(numProcesso);
  }, [buscarProcesso, numProcesso]);

  const handleChangeNumProcesso = useCallback((e) => {
    const { value } = e.target;
    setNumProcesso(value);
  }, []);

  const fecharDialogBloqueio = useCallback(() => {
    setDialogBloqueioFechado(true);
  }, []);

  const handleChangeJustificativa = useCallback((e) => {
    setJustificativa(e.target.value.slice(0, MAX_COUNTER_JUSTIFICATIVA));
  }, []);

  const abrirConfirmacao = useCallback(() => {
    setConfirmacaoAberta(true);
  }, []);

  const fecharConfirmacao = useCallback(() => {
    setConfirmacaoAberta(false);
  }, []);

  const confirmarRetirada = useCallback(() => {
    const idProcesso = numProcesso;
    const motivo = justificativa.trim();

    setLoadingSubmit(true);
    retirarControleExpiracaoDoProcesso(numProcesso, motivo, idUsuario)
      .then(() => {
        toast.success(MENSAGEM_SUCESSO_ALTERACAO);
        const texto = `Processo retirado do controle de expiração, motivo: ${motivo}`;
        inserirNovoHistorico(idProcesso, tiposHistoricos.COMENTARIO_AUTOMATIO.key, texto).catch(() => {
          toast.warn(MENSAGEM_ERRO_HISTORICO_AUTOMATICO);
        });
        return buscarProcesso(numProcesso);
      })
      .catch((exception) => {
        toast.error(getExceptionMessage(exception) || MENSAGEM_ERRO_ALTERACAO);
      })
      .finally(() => {
        setLoadingSubmit(false);
        setConfirmacaoAberta(false);
      });
  }, [numProcesso, justificativa, idUsuario, buscarProcesso]);

  const mensagemBloqueio = controleExpiracaoAtual
    ? obterMensagemBloqueio(controleExpiracaoAtual)
    : null;
  const bloqueado = mensagemBloqueio != null;
  const justificativaValida = possuiMinimoPalavras(justificativa, MINIMO_PALAVRAS_JUSTIFICATIVA);
  const habilitarBotaoRetirar = Boolean(controleExpiracaoAtual) && !bloqueado && justificativaValida;

  return (
    <WrapperSubTitleForm title={TITULO_PAGINA} className={style.pageRoot}>
      <LoadingUI show={loading || loadingSubmit} />
      <Typography>{SUBTITULO_PAGINA}</Typography>

      <form onSubmit={handleSubmitSearchForm}>
        <Grid24 container isFormContainer spacing="1" className={style.formSection}>
          <Grid24 xs={24} sm={6}>
            <NumberInput
              isForm
              fullWidth
              numberType="integer"
              name="numProcesso"
              label={LABEL_NUMERO_PROCESSO}
              value={numProcesso}
              onChange={handleChangeNumProcesso}
              maxLength={MAX_LENGTH_NUM_PROCESSO}
              acceptEmptyValue
            />
          </Grid24>
          <Grid24 xs={24} sm={2}>
            <SearchButton
              className={style.searchButtonCircular}
              type="submit"
              disabled={numProcesso.trim() === '' || loading}
            >
              <SearchIcon />
            </SearchButton>
          </Grid24>
        </Grid24>
      </form>

      {controleExpiracaoAtual && (
        <Grid24 container isFormContainer spacing="1" className={style.formSection}>
          <Grid24 xs={24} sm={10}>
            <InputField
              disabled
              isForm
              fullWidth
              label={LABEL_NOME_CLIENTE}
              value={controleExpiracaoAtual.nomeCliente ?? ''}
            />
          </Grid24>
        </Grid24>
      )}

      {controleExpiracaoAtual && !bloqueado && (
        <>
          <Grid24 container isFormContainer spacing="1" className={style.formSection}>
            <Grid24 xs={24} sm={14}>
              <TextareaField
                isForm
                fullWidth
                label={LABEL_JUSTIFICATIVA}
                name="justificativa"
                value={justificativa}
                onChange={handleChangeJustificativa}
                showCounter
                maxCounter={MAX_COUNTER_JUSTIFICATIVA}
              />
            </Grid24>
          </Grid24>

          <Grid24 container isFormContainer spacing="1" className={style.formSection}>
            <Grid24 xs={24} sm={4}>
              <ButtonUI
                color="success"
                onClick={abrirConfirmacao}
                disabled={!habilitarBotaoRetirar}
              >
                {LABEL_RETIRAR_CONTROLE}
              </ButtonUI>
            </Grid24>
          </Grid24>
        </>
      )}

      <OptionDialogNew
        open={bloqueado && !dialogBloqueioFechado}
        onClickConfirm={fecharDialogBloqueio}
        confirmLabel="OK"
      >
        <p>{mensagemBloqueio}</p>
      </OptionDialogNew>

      <OptionDialogNew
        open={confirmacaoAberta}
        onClickCancel={fecharConfirmacao}
        onClickConfirm={confirmarRetirada}
        confirmLabel="Confirmar"
        cancelLabel="Cancelar"
      >
        <p>{MENSAGEM_CONFIRMACAO_ALTERACAO}</p>
      </OptionDialogNew>
    </WrapperSubTitleForm>
  );
}
